import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import { Modal } from 'atoms';
import { PopupContainer } from 'molecules';
import {
  useMaterialMutations,
  useGroupLessons,
  IGroupMaterial,
} from 'core/hooks';
import { MaterialKindEnum } from 'core/graphql';

import { GroupColors } from './groupColors';
import * as L from '../../_detailShared/detail.styled';

interface IMaterialEditorPopupProps {
  groupId: string;
  /** When provided the popup edits an existing material; otherwise it creates. */
  material?: IGroupMaterial | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Multi-line description field — styled here so the popup keeps to `groupColors` tokens. */
const FormTextarea = styled.textarea`
  min-height: 72px;
  border: 1px solid ${GroupColors.border};
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  background: ${GroupColors.surface};
  outline: none;
  font-weight: 500;
  color: ${GroupColors.textPrimary};
  resize: vertical;
  font-family: inherit;

  &:focus {
    border-color: ${GroupColors.accent};
  }
`;

const EDITOR_KIND_OPTIONS: { value: MaterialKindEnum; key: string }[] = [
  { value: MaterialKindEnum.Homework, key: 'homework' },
  { value: MaterialKindEnum.Reading, key: 'reading' },
  { value: MaterialKindEnum.Video, key: 'video' },
  { value: MaterialKindEnum.Other, key: 'other' },
];

/**
 * MaterialEditorPopup — inline create/edit form for a single group material
 * (TZ §G6). Mirrors `AddStudentToGroupInlinePopup`: a global `Modal` wrapping a
 * `PopupContainer` with the shared `_detailShared` form primitives.
 *
 * Fields map to `CreateMaterialInput`/`UpdateMaterialInput`: lesson selector
 * (`lessonId`), `kind` (`MaterialKindEnum`), `title`, `description`, and
 * `dueDate`. Submission goes through `useMaterialMutations` (`create`/`update`),
 * whose success callback re-fetches the list and closes the popup (Req 16.2).
 *
 * This editor is only ever mounted from `MaterialsTab` behind
 * `useGroupPermissions().canManageMaterials` (which folds in the off-by-default
 * `G6_MATERIALS_CRUD` flag — Req 16.4). The backend remains the authority on
 * each submission (Req 16.5).
 */
export const MaterialEditorPopup = memo(
  ({ groupId, material, onClose, onSaved }: IMaterialEditorPopupProps) => {
    const { t } = useTranslation();
    const isEdit = Boolean(material);

    const { create, update, pending } = useMaterialMutations(() => {
      onSaved();
      onClose();
    });

    const { group } = useGroupLessons(groupId);

    const [lessonId, setLessonId] = useState<string>(
      material?.lesson?.id || '',
    );
    const [kind, setKind] = useState<MaterialKindEnum>(
      material?.kind || MaterialKindEnum.Homework,
    );
    const [title, setTitle] = useState<string>(material?.title || '');
    const [description, setDescription] = useState<string>(
      material?.description || '',
    );
    const [dueDate, setDueDate] = useState<string>(
      material?.dueDate ? moment(material.dueDate).format('YYYY-MM-DD') : '',
    );

    const lessonOptions = useMemo(() => {
      const lessons = group?.lessons || [];
      return lessons
        .filter((l): l is NonNullable<typeof l> => Boolean(l))
        .map((l) => ({
          value: l.id,
          label: `${l.lessonNumber ?? l.number ?? '—'}. ${l.name || ''}${
            l.date ? ` · ${moment(l.date).format('DD.MM.YYYY')}` : ''
          }`,
        }));
    }, [group]);

    // For a new material the backend requires a lesson; editing may leave it as-is.
    const canSubmit =
      Boolean(title.trim()) && (isEdit || Boolean(lessonId)) && !pending;

    const onSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;

        const trimmedTitle = title.trim();
        const trimmedDescription = description.trim() || undefined;
        const normalizedDueDate = dueDate || undefined;

        if (material) {
          await update(material.id, {
            lessonId: lessonId || undefined,
            kind,
            title: trimmedTitle,
            description: trimmedDescription,
            dueDate: normalizedDueDate,
          });
        } else {
          await create({
            groupId,
            lessonId,
            kind,
            title: trimmedTitle,
            description: trimmedDescription,
            dueDate: normalizedDueDate,
          });
        }
      },
      [
        canSubmit,
        title,
        description,
        dueDate,
        material,
        update,
        lessonId,
        kind,
        create,
        groupId,
      ],
    );

    return (
      <Modal>
        <PopupContainer
          width="520px"
          title={t(
            isEdit
              ? 'pages.groupDetail.materials.editor.editTitle'
              : 'pages.groupDetail.materials.editor.createTitle',
          )}
          onClose={onClose}
        >
          <form onSubmit={onSubmit}>
            <L.FormBody>
              <L.FormLabel>
                {t('pages.groupDetail.materials.editor.lesson')}
                <L.FormSelect
                  value={lessonId}
                  onChange={(e) => setLessonId(e.target.value)}
                >
                  <option value="">
                    {t('pages.groupDetail.materials.editor.lessonPlaceholder')}
                  </option>
                  {lessonOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </L.FormSelect>
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.groupDetail.materials.editor.kind')}
                <L.FormSelect
                  value={kind}
                  onChange={(e) => setKind(e.target.value as MaterialKindEnum)}
                >
                  {EDITOR_KIND_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(`pages.groupDetail.materials.kind.${opt.key}`)}
                    </option>
                  ))}
                </L.FormSelect>
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.groupDetail.materials.editor.titleField')}
                <L.FormInput
                  autoFocus
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t(
                    'pages.groupDetail.materials.editor.titlePlaceholder',
                  )}
                  required
                />
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.groupDetail.materials.editor.description')}
                <FormTextarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t(
                    'pages.groupDetail.materials.editor.descriptionPlaceholder',
                  )}
                />
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.groupDetail.materials.editor.dueDate')}
                <L.FormInput
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </L.FormLabel>

              <L.FormFooter>
                <L.PrimaryActionButton
                  type="button"
                  onClick={onClose}
                  disabled={pending}
                >
                  {t('pages.groupDetail.materials.editor.cancel')}
                </L.PrimaryActionButton>
                <L.PrimaryActionButton
                  type="submit"
                  tone="primary"
                  disabled={!canSubmit}
                >
                  {pending
                    ? t('pages.groupDetail.materials.editor.saving')
                    : t('pages.groupDetail.materials.editor.save')}
                </L.PrimaryActionButton>
              </L.FormFooter>
            </L.FormBody>
          </form>
        </PopupContainer>
      </Modal>
    );
  },
);
